import React, { useState, useEffect, useRef } from "react";
import {
  Link,
  useNavigate,
  useLocation as useRouterLocation,
} from "react-router-dom";
import * as bootstrap from "bootstrap";
import LocationDropdown from "./LocationDropdown";
import { useBooking } from "../contexts/BookingContext";
import { useLocation } from "../contexts/LocationContext";
import PhoneVerificationPopup from "./PhoneVerificationPopup";
import RegistrationPopup from "./RegistrationPopup";
import OTPEntryPopup from "./OTPEntryPopup";
import QuickBookNew from "./QuickBookNew";
import AgeWarningModal from "./AgeWarningModal";
import "./Header.css";
import "./headerbook.css";

const Header = () => {
  const { userDetails, setUserDetails } = useBooking();
  const { formatDistance } = useLocation();

  // ── Auth state ────────────────────────────────────────────────────────────
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false);
  const [showOTPPopup, setShowOTPPopup] = useState(false);
  const [registrationPhone, setRegistrationPhone] = useState("");
  const [otpPhone, setOtpPhone] = useState("");

  // ── Search state ──────────────────────────────────────────────────────────
  const [movies, setMovies] = useState([]);
  const [cinemas, setCinemas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef(null);
  const searchRef = useRef(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [shouldKeepHeaderHidden, setShouldKeepHeaderHidden] = useState(false);
  const [mobileQBOpen, setMobileQBOpen] = useState(false);
  const [pendingAgeWarning, setPendingAgeWarning] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);

  const [eventsOpen, setEventsOpen] = useState(false);
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();

  // ── Route-based visibility helpers ───────────────────────────────────────
  const shouldHideSearch = () => {
    const path = routerLocation.pathname;
    return (
      path === "/payment" ||
      path === "/snacks" ||
      path.startsWith("/seat-selection")
    );
  };

  const shouldHideQuickBook = () => {
    const path = routerLocation.pathname;
    return (
      path === "/payment" ||
      path === "/snacks" ||
      path.startsWith("/seat-selection") ||
      path.startsWith("/booking")
    );
  };

  const shouldHideAuthElements = () => {
    const path = routerLocation.pathname;

    return (
      isMobile &&
      (path === "/payment" ||
        path === "/snacks" ||
        path.startsWith("/seat-selection"))
    );
  };
  const eventsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        window.innerWidth >= 1200 &&
        eventsRef.current &&
        !eventsRef.current.contains(e.target)
      ) {
        setEventsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 576);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-open events dropdown in tablet view
  useEffect(() => {
    if (window.innerWidth < 1200) {
      setEventsOpen(true);
    }
  }, []);

  // ── Tawk hide/show on offcanvas ───────────────────────────────────────────
  useEffect(() => {
    const offcanvasEl = document.getElementById("offcanvasMenu");

    if (!offcanvasEl) return;

    const handleOpen = () => {
      if (window.Tawk_API && window.Tawk_API.hideWidget) {
        window.Tawk_API.hideWidget();
      }
    };

    const handleClose = () => {
      if (window.Tawk_API && window.Tawk_API.showWidget) {
        window.Tawk_API.showWidget();
      }
      document
        .querySelectorAll(".offcanvas-backdrop")
        .forEach((b) => b.remove());
      document.body.classList.remove("offcanvas-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.paddingRight = "";
    };

    offcanvasEl.addEventListener("show.bs.offcanvas", handleOpen);
    offcanvasEl.addEventListener("hidden.bs.offcanvas", handleClose);

    return () => {
      offcanvasEl.removeEventListener("show.bs.offcanvas", handleOpen);
      offcanvasEl.removeEventListener("hidden.bs.offcanvas", handleClose);
    };
  }, []);

  const shouldHideHeaderElements = () =>
    (isSearchFocused || shouldKeepHeaderHidden) && isMobile;

  // Reset header hidden state when navigating to home
  useEffect(() => {
    if (routerLocation.pathname === "/") {
      setShouldKeepHeaderHidden(false);
    }
  }, [routerLocation.pathname]);

  // Toggle body class for global search focus styling
  useEffect(() => {
    if ((isSearchFocused || shouldKeepHeaderHidden) && isMobile) {
      document.body.classList.add("search-focused");
    } else {
      document.body.classList.remove("search-focused");
    }
    return () => document.body.classList.remove("search-focused");
  }, [isSearchFocused, shouldKeepHeaderHidden, isMobile]);

  // ── Search click-outside handler ─────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Fetch movies for header search ───────────────────────────────────────
  useEffect(() => {
    const fetchMovies = async () => {
      try {
        const { api } = await import("../config/api");
        const allMovies = await api.getMovies({ limit: 100 });
        const normalized = Array.isArray(allMovies)
          ? allMovies
              .map((movie) => ({
                ...movie,
                poster:
                  movie.poster ||
                  movie.image ||
                  movie.banner ||
                  movie.posterUrl ||
                  "",
                language: movie.language || "",
                isActive:
                  movie.isActive === true ||
                  movie.isActive === 1 ||
                  movie.status === "Now Showing",
                isNowShowing:
                  movie.isNowShowing || movie.status === "Now Showing",
                isComingSoon:
                  movie.isComingSoon || movie.status === "Coming Soon",
              }))
              .sort((a, b) => a.title.localeCompare(b.title))
          : [];
        setMovies(normalized);
      } catch (error) {
        console.error("Error fetching movies for search:", error);
        setMovies([]);
      }
    };
    fetchMovies();
  }, []);

  // ── Fetch cinemas for header search ──────────────────────────────────────
  useEffect(() => {
    const fetchCinemas = async () => {
      try {
        const { api } = await import("../config/api");
        const allCinemas = await api.getCinemas({ limit: 100, sortBy: "name" });
        setCinemas(Array.isArray(allCinemas) ? allCinemas : []);
      } catch (error) {
        console.error("Error fetching cinemas for search:", error);
        setCinemas([]);
      }
    };
    fetchCinemas();
  }, []);

  // Helper to normalize genre
  const formatGenre = (genre) => {
    if (!genre) return "";
    if (Array.isArray(genre)) return genre.join(", ");
    if (typeof genre === "string") {
      // Handle JSON-encoded arrays like '{"Action","Comedy"}'
      try {
        const parsed = JSON.parse(genre.replace(/\{/, "[").replace(/\}/, "]"));
        if (Array.isArray(parsed)) return parsed.join(", ");
      } catch {}
      // Handle comma-separated or plain strings
      return genre.replace(/[{}"]/g, "").trim();
    }
    return String(genre);
  };

  // ── Search handlers ───────────────────────────────────────────────────────
  const handleSearch = (query) => {
    setSearchQuery(query);

    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      if (isMobile && isSearchFocused) setShouldKeepHeaderHidden(true);
      return;
    }

    const results = [];

    movies.forEach((movie) => {
      if (!movie.isNowShowing && !movie.isComingSoon) return;
      if (
        movie.title.toLowerCase().includes(query.toLowerCase()) ||
        (movie.genre &&
          movie.genre.toLowerCase().includes(query.toLowerCase())) ||
        (movie.language &&
          movie.language.toLowerCase().includes(query.toLowerCase()))
      ) {
        results.push({
          type: "movie",
          id: movie.id,
          title: movie.title,
          subtitle: `${formatGenre(movie.genre)} • ${movie.language || ""}`,
          image: movie.poster || movie.image,
        });
      }
    });

    cinemas.forEach((cinema) => {
      if (
        (cinema.name &&
          cinema.name.toLowerCase().includes(query.toLowerCase())) ||
        (cinema.location &&
          cinema.location.toLowerCase().includes(query.toLowerCase()))
      ) {
        let subtitle = cinema.address || cinema.location || cinema.city || "";
        if (cinema.distance != null && formatDistance) {
          subtitle += ` • ${formatDistance(cinema.distance)}`;
        }
        results.push({
          type: "theater",
          id: cinema.id,
          title: cinema.name,
          subtitle,
          image: null,
        });
      }
    });

    if (isMobile) setShouldKeepHeaderHidden(true);
    setSearchResults(results);
    setShowResults(true);
  };

  // const handleResultClick = (result) => {
  //   searchInputRef.current?.blur();
  //   if (isMobile) setShouldKeepHeaderHidden(true);
  //   if (result.type === "movie") {
  //     navigate(`/booking/${result.id}`);
  //   } else if (result.type === "theater") {
  //     navigate(`/cinema/${result.id}`);
  //   }
  //   setShowResults(false);
  //   setSearchQuery("");
  // };
  const handleResultClick = (result) => {
    searchInputRef.current?.blur();
    if (isMobile) setShouldKeepHeaderHidden(true);
    setShowResults(false);
    setSearchQuery("");

    if (result.type === "movie") {
      // Find the movie to check age rating
      const movie = movies.find((m) => m.id === result.id);
      const ageRating = movie?.ageRating || movie?.rating || "";
      if (ageRating.toUpperCase() === "A") {
        setPendingAgeWarning(result.id); // hold navigation
        return;
      }
      navigate(`/booking/${result.id}`);
    } else if (result.type === "theater") {
      navigate(`/cinema/${result.id}`);
    }
  };

  const getImageSource = (imageUrl) =>
    imageUrl && imageUrl.trim()
      ? imageUrl
      : "/assets/img/movie-placeholder.png";

  // ── Auth handlers ─────────────────────────────────────────────────────────
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    closeOffcanvasMenu();
    setTimeout(() => setShowPhonePopup(true), 250);
  };

  const handlePhoneVerified = (phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/\s+/g, "").replace(/-/g, "");
    setOtpPhone(cleanPhone);
    setShowPhonePopup(false);
    setShowOTPPopup(true);
    setUserDetails((prev) => ({
      ...prev,
      phone: cleanPhone,
      mobile: cleanPhone,
      verified: false,
    }));
  };

  const handleOTPVerified = (user) => {
    setShowOTPPopup(false);
    const finalPhone = user.mobile || user.phone || otpPhone || "";
    const userDetailsToSave = {
      userId: user.userId || user.id,
      id: user.userId || user.id,
      name: user.name || "",
      email: user.email || "",
      phone: finalPhone,
      mobile: finalPhone,
      verified: true,
    };
    setUserDetails(userDetailsToSave);
    localStorage.setItem("user", JSON.stringify(userDetailsToSave));
  };

  const handleGoogleSignIn = (user) => {
    setShowPhonePopup(false);
    // user object comes from /api/auth/google/verify or /api/auth/google/link
    // with decrypted name/email/mobile already
    const userDetailsToSave = {
      userId: user.userId || user.id,
      id: user.userId || user.id,
      name: user.name || "",
      email: user.email || "",
      phone: user.mobile || user.phone || "",
      mobile: user.mobile || user.phone || "",
      authProvider: user.authProvider || "google",
      googleId: user.googleId || null,
      profilePictureUrl: user.profilePictureUrl || null,
      verified: true,
    };
    setUserDetails(userDetailsToSave);
    localStorage.setItem("user", JSON.stringify(userDetailsToSave));
  };

  const handleShowRegistration = (phoneNumber) => {
    setRegistrationPhone(phoneNumber);
    setShowPhonePopup(false);
    setShowRegistrationPopup(true);
  };

  const handleRegistrationComplete = (userData) => {
    const userWithPhone = {
      userId: userData.userId,
      id: userData.userId,
      name: userData.name,
      email: userData.email,
      phone: userData.mobile,
      mobile: userData.mobile,
      verified: true,
    };
    setUserDetails(userWithPhone);
    localStorage.setItem("user", JSON.stringify(userWithPhone));
    setShowRegistrationPopup(false);
    setShowOTPPopup(false);
  };

  const handleClosePhonePopup = () => setShowPhonePopup(false);
  const handleCloseOTPPopup = () => setShowOTPPopup(false);
  const handleCloseRegistrationPopup = () => setShowRegistrationPopup(false);
  const handleResendOTP = async () => {};

  // ── Offcanvas menu handler ───────────────────────────────────────────────
  // Hard-cleanup: forces the offcanvas and any backdrops out of the DOM
  // synchronously and resets all body/html styles that Bootstrap applies
  // when an offcanvas is open. This prevents the offcanvas (which stays in
  // the DOM during its 300ms close animation with position:fixed z-index:1045)
  // from intercepting touch events on the newly-navigated page on mobile.
  const closeOffcanvasMenu = () => {
    const offcanvasElement = document.getElementById("offcanvasMenu");
    if (!offcanvasElement) return;

    const instance =
      bootstrap.Offcanvas.getInstance(offcanvasElement) ||
      bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement);
    instance.hide();

    offcanvasElement.classList.remove("show", "showing");
    offcanvasElement.style.visibility = "hidden";
    offcanvasElement.style.transition = "none";

    document
      .querySelectorAll(".offcanvas-backdrop")
      .forEach((b) => b.remove());

    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.body.classList.remove("offcanvas-open");
    document.documentElement.style.overflow = "";
    document.documentElement.style.paddingRight = "";
  };

  // ── Offcanvas nav helper ─────────────────────────────────────────────────
  // On mobile, the offcanvas close animation races with React Router's
  // navigation, causing the half-closed offcanvas to block touch events on
  // the new page (no scroll possible until a refresh). To eliminate this
  // race condition, on mobile we use window.location.href for a hard
  // navigation that fully resets the DOM — same effect as a refresh but
  // scoped only to menu-link navigation.
  const handleOffcanvasNav = (e, to) => {
    closeOffcanvasMenu();
    if (window.innerWidth < 1200) {
      e.preventDefault();
      // Use a microtask delay so React can flush any pending state updates
      // (e.g. setEventsOpen(false)) before the page transitions.
      setTimeout(() => {
        window.location.href = to;
      }, 30);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const isSearchActive = isMobileSearchOpen || searchQuery.length >= 2;
  const hideQuickBook = shouldHideQuickBook();
  useEffect(() => {
    const openQB = () => {
      if (window.innerWidth <= 576) {
        setMobileQBOpen(true);
        document.body.classList.add("qb-open");
      }
    };

    window.addEventListener("openMobileQB", openQB);
    return () => window.removeEventListener("openMobileQB", openQB);
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <header
        className={`main-header ${shouldHideHeaderElements() ? "search-focused" : ""}`}
      >
        <nav className="navbar navbar-expand-xl">
          <div className="container">
            {/* LOGO */}
            <Link
              className="navbar-brand"
              to="/"
              onClick={(e) => {
                // If already on home page, scroll to top
                if (routerLocation.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo(0, 0);
                }
              }}
            >
              <img src="/assets/img/logo.svg" alt="Logo" />
            </Link>

            {/* MOBILE LOCATION */}
            <div className="mob-view d-xl-none">
              <LocationDropdown mobile />
            </div>

            {/* MOBILE RIGHT SIDE ACTIONS */}
            <div className="d-flex align-items-center ms-auto gap-2 d-xl-none mobile-header-actions">
              {/* AUTH BUTTONS ON MOBILE HEADER */}
              {!shouldHideAuthElements() && (
                <div className="mobile-auth-buttons">
                  {userDetails?.verified ? (
                    <Link
                      to="/profile?tab=personal-details"
                      className="secondary-btn mobile-profile-btn"
                    >
                      {userDetails.name?.split(" ")[0] || "User"}
                    </Link>
                  ) : (
                    <>
                      <button
                        className="secondary-btn mobile-auth-btn"
                        onClick={handleLoginClick}
                      >
                        Register
                      </button>

                      <button
                        className="secondary-btn mobile-auth-btn"
                        onClick={handleLoginClick}
                      >
                        Login
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* HAMBURGER */}
              <button
                type="button"
                className="mobile-menu-btn"
                onClick={() => {
                  const el = document.getElementById("offcanvasMenu");
                  if (!el) return;
                  bootstrap.Offcanvas.getOrCreateInstance(el).show();
                }}
                aria-controls="offcanvasMenu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>

            {/* OFFCANVAS MENU */}
            <div
              className="offcanvas offcanvas-end text-bg-dark header-actions"
              id="offcanvasMenu"
              tabIndex="-1"
            >
              <div className="offcanvas-header">
                <Link
                  className="navbar-brand"
                  to="/"
                  onClick={(e) => {
                    closeOffcanvasMenu();
                    // If already on home page, scroll to top
                    if (routerLocation.pathname === "/") {
                      e.preventDefault();
                      window.scrollTo(0, 0);
                    }
                  }}
                >
                  <img src="/assets/img/logo.svg" alt="Logo" />
                </Link>
                <button
                  type="button"
                  className="mobile-nav-close"
                  data-bs-dismiss="offcanvas"
                  aria-label="Close"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>

              <div className="offcanvas-body">
                <div className="offcanvas-scroll">
                <ul className="d-flex align-items-center menu-items">
                  <li>
                    <Link
                      to="/"
                      className={
                        routerLocation.pathname === "/" ? "active" : ""
                      }
                      onClick={(e) => {
                        closeOffcanvasMenu();
                        // If already on home page, scroll to top
                        if (routerLocation.pathname === "/") {
                          e.preventDefault();
                          window.scrollTo(0, 0);
                        }
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_478_766)">
                          <path
                            d="M11.0833 13.9997H2.91667C1.30842 13.9997 0 12.6913 0 11.083V5.67203C0 4.70078 0.480667 3.79661 1.28567 3.25411L5.36842 0.498445C6.3595 -0.170055 7.6405 -0.170055 8.63158 0.498445L12.7149 3.25411C13.5193 3.79661 14 4.7002 14 5.67203V11.083C14 12.6913 12.6916 13.9997 11.0833 13.9997ZM7 1.16461C6.65933 1.16461 6.31867 1.26495 6.02117 1.4662L1.93783 4.22128C1.45483 4.54678 1.16667 5.0887 1.16667 5.67145V11.0824C1.16667 12.0473 1.95183 12.8324 2.91667 12.8324H11.0833C12.0482 12.8324 12.8333 12.0473 12.8333 11.0824V5.67203C12.8333 5.08928 12.5452 4.54678 12.0627 4.22186L7.97883 1.4662C7.68133 1.26495 7.34067 1.16461 7 1.16461Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_478_766">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Home
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/show-times"
                      className={
                        routerLocation.pathname === "/show-times"
                          ? "active"
                          : ""
                      }
                      onClick={(e) => handleOffcanvasNav(e, "/show-times")}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.25 12.25H1.75C0.783417 12.25 0 11.4666 0 10.5V8.21217L0.639333 8.21392C1.281 8.197 1.79783 7.66967 1.79783 7.02392C1.79783 6.37817 1.28217 5.852 0.641667 5.83392L0 5.83567V3.5C0 2.53342 0.783417 1.75 1.75 1.75H12.25C13.2166 1.75 14 2.53342 14 3.5V5.83333H13.482C12.9004 5.83333 12.3608 6.22942 12.2663 6.80283C12.1462 7.53317 12.7085 8.16667 13.4167 8.16667H14V10.5C14 11.4666 13.2166 12.25 12.25 12.25ZM1.75 11.0833H12.25C12.572 11.0833 12.8333 10.822 12.8333 10.5V9.25983C11.8282 9.00025 11.0833 8.08558 11.0833 7C11.0833 5.91442 11.8282 5.00033 12.8333 4.74017V3.5C12.8333 3.178 12.572 2.91667 12.25 2.91667H1.75C1.428 2.91667 1.16667 3.178 1.16667 3.5V4.73375C2.19742 4.98575 2.9645 5.91675 2.9645 7.02392C2.9645 8.13108 2.19742 9.06208 1.16667 9.31408V10.5C1.16667 10.822 1.428 11.0833 1.75 11.0833Z"
                          fill="white"
                        />
                      </svg>
                      Show Times
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/cinemas"
                      className={
                        routerLocation.pathname === "/cinemas" ? "active" : ""
                      }
                      onClick={(e) => handleOffcanvasNav(e, "/cinemas")}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_478_776)">
                          <g clipPath="url(#clip1_478_776)">
                            <path
                              d="M12.8338 6.41689H3.17904L12.5445 3.98439C13.1587 3.82514 13.5291 3.21498 13.3815 2.56923L13.1774 1.83773C12.9306 0.565477 11.5341 -0.285607 10.2922 0.0807266L1.73471 2.36856C0.514378 2.69639 -0.229372 3.96106 0.0797943 5.19656L0.583794 7.07664V11.0836C0.583794 12.6918 1.89221 14.0002 3.50046 14.0002H11.0838C12.692 14.0002 14.0005 12.6918 14.0005 11.0836V7.58356C14.0005 6.94014 13.4772 6.41689 12.8338 6.41689ZM6.67613 2.25539L5.11221 4.71006L3.54479 5.11723L5.10638 2.67481L6.67613 2.25539ZM10.067 1.34948L8.52413 3.82398L6.77004 4.27956L8.34388 1.80973L10.067 1.34948ZM12.2516 2.85564L10.165 3.39756L11.4612 1.31856C11.4752 1.32614 11.4904 1.32906 11.5038 1.33723C11.7797 1.50464 11.9687 1.77064 12.0451 2.11831L12.2516 2.85564ZM2.03629 3.49498L3.43513 3.12106L1.88288 5.54831L1.41446 5.67023L1.20854 4.90489C1.05571 4.29181 1.42729 3.65889 2.03629 3.49498ZM12.8338 11.0836C12.8338 12.0484 12.0486 12.8336 11.0838 12.8336H3.50046C2.53563 12.8336 1.75046 12.0484 1.75046 11.0836V7.58356H12.8338V11.0836Z"
                              fill="white"
                            />
                          </g>
                        </g>
                        <defs>
                          <clipPath id="clip0_478_776">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                          <clipPath id="clip1_478_776">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Cinemas
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/offers"
                      className={
                        routerLocation.pathname === "/offers" ? "active" : ""
                      }
                      onClick={(e) => handleOffcanvasNav(e, "/offers")}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_478_782)">
                          <path
                            d="M7 14C6.0515 14 5.15667 13.5835 4.54533 12.8573C3.647 12.9605 2.72125 12.6204 2.05042 11.9502C1.38017 11.2793 1.04183 10.3518 1.12292 9.40567C0.4165 8.84333 0 7.9485 0 7C0 6.0515 0.4165 5.15667 1.14333 4.54533C1.04125 3.64875 1.37958 2.72125 2.05042 2.05042C2.72125 1.379 3.647 1.04125 4.59433 1.12292C5.15667 0.417083 6.0515 0 7 0C7.9485 0 8.84333 0.4165 9.45467 1.14275C10.3542 1.04008 11.2788 1.37958 11.9496 2.04983C12.6198 2.72067 12.9582 3.64817 12.8771 4.59433C13.5835 5.15667 14 6.0515 14 7C14 7.9485 13.5835 8.84333 12.8567 9.45467C12.9587 10.3512 12.6204 11.2788 11.9496 11.9496C11.2782 12.6204 10.3507 12.9582 9.40567 12.8771C8.84333 13.5829 7.9485 14 7 14ZM4.59258 11.711C4.91983 11.711 5.22317 11.8516 5.43725 12.1053C5.82692 12.5679 6.39625 12.8333 7 12.8333C7.60375 12.8333 8.17308 12.5679 8.56275 12.1053C8.79783 11.8259 9.14025 11.6847 9.50542 11.7145C10.1086 11.7653 10.6977 11.5512 11.1247 11.1242C11.5512 10.6977 11.7664 10.1074 11.7151 9.50483C11.6836 9.14083 11.8259 8.79725 12.1059 8.56158C12.5679 8.1725 12.8333 7.60258 12.8333 6.99942C12.8333 6.39625 12.5679 5.82633 12.1059 5.43725C11.8265 5.20217 11.6836 4.858 11.7151 4.494C11.7664 3.89142 11.5517 3.30108 11.1247 2.87467C10.6977 2.44825 10.1051 2.23533 9.506 2.28433C9.14083 2.31758 8.79783 2.17292 8.56275 1.89408C8.17308 1.4315 7.60375 1.16608 7 1.16608C6.39625 1.16608 5.82692 1.4315 5.43725 1.89408C5.20158 2.1735 4.858 2.3135 4.49458 2.28492C3.8885 2.23242 3.30225 2.44825 2.87525 2.87525C2.44883 3.30167 2.23358 3.892 2.28492 4.49458C2.31642 4.85858 2.17408 5.20217 1.89408 5.43783C1.43208 5.82692 1.16667 6.39683 1.16667 7C1.16667 7.60317 1.43208 8.17308 1.89408 8.56217C2.1735 8.79725 2.31642 9.14142 2.28492 9.50542C2.23358 10.108 2.44825 10.6983 2.87525 11.1247C3.30225 11.5517 3.89783 11.7652 4.494 11.7151C4.52725 11.7122 4.55992 11.711 4.59258 11.711ZM5.25 4.66667C4.928 4.66667 4.66667 4.928 4.66667 5.25C4.66667 5.572 4.928 5.83333 5.25 5.83333C5.572 5.83333 5.83333 5.572 5.83333 5.25C5.83333 4.928 5.572 4.66667 5.25 4.66667ZM8.75 8.16667C8.428 8.16667 8.16667 8.428 8.16667 8.75C8.16667 9.072 8.428 9.33333 8.75 9.33333C9.072 9.33333 9.33333 9.072 9.33333 8.75C9.33333 8.428 9.072 8.16667 8.75 8.16667ZM6.31867 9.07375L8.652 5.57375C8.83108 5.306 8.75875 4.94317 8.49042 4.76467C8.2215 4.585 7.85983 4.6585 7.68133 4.92625L5.348 8.42625C5.16892 8.694 5.24125 9.05683 5.50958 9.23533C5.60933 9.30183 5.72133 9.33333 5.83275 9.33333C6.02117 9.33333 6.20667 9.24233 6.31867 9.07375Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_478_782">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Offers
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/advertise-with-us"
                      className={
                        routerLocation.pathname === "/advertise-with-us"
                          ? "active"
                          : ""
                      }
                      onClick={(e) =>
                        handleOffcanvasNav(e, "/advertise-with-us")
                      }
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_478_787)">
                          <g clipPath="url(#clip1_478_787)">
                            <path
                              d="M9.29367 0.970884C9.093 0.4033 8.24542 0.514717 8.17017 1.10213C8.06517 2.04422 7.67433 2.9163 5.54167 2.9163H2.33333C1.0465 2.91688 0 3.96338 0 5.25022V6.41688C0 7.3088 0.508667 8.07588 1.24542 8.46847L2.961 12.3342C3.25267 12.9916 3.90542 13.4163 4.62525 13.4163C5.24125 13.4163 5.81058 13.1089 6.14775 12.5932C6.48492 12.0776 6.53975 11.4336 6.29358 10.8683L5.37075 8.74905H5.54108C7.67375 8.74905 8.06458 9.62113 8.16958 10.5632C8.26117 11.272 9.31525 11.209 9.33275 10.4991V1.16688C9.3205 1.10155 9.31175 1.03505 9.29308 0.970884H9.29367ZM5.22433 11.335C5.31417 11.5409 5.29492 11.7666 5.17183 11.9545C4.90817 12.3587 4.22392 12.3033 4.02792 11.8611L2.64717 8.75022H4.0985L5.22433 11.335ZM5.54167 7.58297H2.33333C1.68992 7.58297 1.16667 7.05972 1.16667 6.4163V5.24963C1.16667 4.60622 1.68992 4.08297 2.33333 4.08297H5.54167C6.66342 4.08297 7.53725 3.85138 8.16667 3.39872V8.2678C7.53725 7.81513 6.66342 7.58355 5.54167 7.58355V7.58297ZM12.6332 9.13288C12.5195 9.24663 12.3702 9.3038 12.2208 9.3038C12.0715 9.3038 11.9222 9.24663 11.8084 9.13288C11.5803 8.9048 11.5803 8.53613 11.8084 8.30805C13.1728 6.94363 13.1728 4.72288 11.8084 3.35847C11.5803 3.13038 11.5803 2.76172 11.8084 2.53363C12.0365 2.30555 12.4052 2.30555 12.6332 2.53363C14.4527 4.35305 14.4527 7.31347 12.6332 9.13288ZM10.9182 7.65355C10.8045 7.7673 10.6552 7.82447 10.5058 7.82447C10.3565 7.82447 10.2072 7.7673 10.0934 7.65355C9.86533 7.42547 9.86533 7.0568 10.0934 6.82872C10.6423 6.2798 10.6423 5.38613 10.0934 4.83722C9.86533 4.60913 9.86533 4.24047 10.0934 4.01238C10.3215 3.7843 10.6902 3.7843 10.9182 4.01238C11.9222 5.0163 11.9222 6.64963 10.9182 7.65355Z"
                              fill="white"
                            />
                          </g>
                        </g>
                        <defs>
                          <clipPath id="clip0_478_787">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                          <clipPath id="clip1_478_787">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Advertise
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/about-us"
                      className={
                        routerLocation.pathname === "/about-us" ||
                        routerLocation.pathname === "/page/about-us"
                          ? "active"
                          : ""
                      }
                      onClick={(e) => handleOffcanvasNav(e, "/about-us")}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_478_800)">
                          <path
                            d="M7 0C3.14017 0 0 3.14017 0 7C0 10.8598 3.14017 14 7 14C10.8598 14 14 10.8598 14 7C14 3.14017 10.8598 0 7 0ZM7 12.8333C3.77825 12.8333 1.16667 10.2218 1.16667 7C1.16667 3.77825 3.77825 1.16667 7 1.16667C10.2218 1.16667 12.8333 3.77825 12.8333 7C12.8333 10.2218 10.2218 12.8333 7 12.8333Z"
                            fill="white"
                          />
                          <path
                            d="M7 3.5C6.32117 3.5 5.83333 3.98783 5.83333 4.66667C5.83333 5.3455 6.32117 5.83333 7 5.83333C7.67883 5.83333 8.16667 5.3455 8.16667 4.66667C8.16667 3.98783 7.67883 3.5 7 3.5Z"
                            fill="white"
                          />
                          <path
                            d="M7 7C6.67733 7 6.41667 7.26067 6.41667 7.58333V10.5C6.41667 10.8227 6.67733 11.0833 7 11.0833C7.32267 11.0833 7.58333 10.8227 7.58333 10.5V7.58333C7.58333 7.26067 7.32267 7 7 7Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_478_800">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      About Us
                    </Link>
                  </li>

                  <li className="events-menu" ref={eventsRef}>
                    <Link
                      to="#"
                      className={`event-link ${
                        routerLocation.pathname === "/mamangam-hall-booking" ||
                        routerLocation.pathname ===
                          "/special-screening-bulk-booking" ||
                        routerLocation.pathname === "/page/auditoriumrental"
                          ? "active"
                          : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (window.innerWidth >= 1200) {
                          setEventsOpen((prev) => !prev);
                        }
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_478_793)">
                          <path
                            d="M4.66667 7H3.5C2.85658 7 2.33333 7.52325 2.33333 8.16667V9.33333C2.33333 9.97675 2.85658 10.5 3.5 10.5H4.66667C5.31008 10.5 5.83333 9.97675 5.83333 9.33333V8.16667C5.83333 7.52325 5.31008 7 4.66667 7ZM3.5 9.33333V8.16667H4.66667V9.33333H3.5ZM11.0833 1.16667H10.5V0.583333C10.5 0.261333 10.2392 0 9.91667 0C9.59408 0 9.33333 0.261333 9.33333 0.583333V1.16667H4.66667V0.583333C4.66667 0.261333 4.40592 0 4.08333 0C3.76075 0 3.5 0.261333 3.5 0.583333V1.16667H2.91667C1.30842 1.16667 0 2.47508 0 4.08333V11.0833C0 12.6916 1.30842 14 2.91667 14H11.0833C12.6916 14 14 12.6916 14 11.0833V4.08333C14 2.47508 12.6916 1.16667 11.0833 1.16667ZM2.91667 2.33333H11.0833C12.0482 2.33333 12.8333 3.1185 12.8333 4.08333V4.66667H1.16667V4.08333C1.16667 3.1185 1.95183 2.33333 2.91667 2.33333ZM11.0833 12.8333H2.91667C1.95183 12.8333 1.16667 12.0482 1.16667 11.0833V5.83333H12.8333V11.0833C12.8333 12.0482 12.0482 12.8333 11.0833 12.8333Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_478_793">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Events
                      {/* Arrow only on desktop */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`events-arrow ${eventsOpen ? "open" : ""}`}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </Link>

                    {/* Mobile: always show | Desktop: toggle */}
                    {(eventsOpen || window.innerWidth < 1200) && (
                      <ul className="events-dropdown events-open">
                        <li>
                          <Link
                            className={
                              routerLocation.pathname ===
                              "/mamangam-hall-booking"
                                ? "dropdown-item active"
                                : "dropdown-item"
                            }
                            to="/mamangam-hall-booking"
                            onClick={(e) =>
                              handleOffcanvasNav(e, "/mamangam-hall-booking")
                            }
                          >
                            Mamangam Hall Booking
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={
                              routerLocation.pathname ===
                              "/special-screening-bulk-booking"
                                ? "dropdown-item active"
                                : "dropdown-item"
                            }
                            to="/special-screening-bulk-booking"
                            onClick={(e) =>
                              handleOffcanvasNav(
                                e,
                                "/special-screening-bulk-booking",
                              )
                            }
                          >
                            Special Screening & Bulk Booking
                          </Link>
                        </li>
                        {/* Auditorium Rental link commented out
                        <li>
                          <Link
                            className={routerLocation.pathname === "/page/auditoriumrental" ? "dropdown-item active" : "dropdown-item"}
                            to="/page/auditoriumrental"
                            onClick={() => {
                              if (window.innerWidth >= 1200)
                                setEventsOpen(false);
                              closeOffcanvasMenu();
                            }}
                          >
                            Auditorium Rental
                          </Link>
                        </li>
                        */}
                      </ul>
                    )}
                  </li>
                </ul>

                {/* DESKTOP SEARCH */}
                {!shouldHideSearch() && (
                  <form
                    ref={searchRef}
                    className="d-flex ms-auto search-wrapper"
                    role="search"
                  >
                    <div className="position-relative w-100">
                      <input
                        className="form-control th-search"
                        ref={searchInputRef}
                        type="search"
                        placeholder=""
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        onFocus={(e) => {
                          if (e.target.closest(".boking")) return;
                          setIsSearchFocused(true);
                          if (searchResults.length > 0) setShowResults(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setIsSearchFocused(false);
                            setShowResults(false);
                          }, 200);
                        }}
                      />

                      {showResults && searchResults.length > 0 && (
                        <div className="search-results">
                          {searchResults.map((result, index) => (
                            <div
                              key={`${result.type}-${result.id}-${index}`}
                              className="search-result-item"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleResultClick(result);
                              }}
                            >
                              <div className="result-image-wrapper">
                                <img
                                  src={getImageSource(result.image)}
                                  alt={result.title}
                                  className="result-image"
                                />
                              </div>
                              <div className="result-content">
                                <div className="result-title">
                                  {result.title}
                                </div>
                                <div className="result-subtitle">
                                  {result.subtitle}
                                </div>
                                <div className="result-type">
                                  {result.type === "movie" ? "Movie" : "Cinema"}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </form>
                )}

                {/* LOCATION (Desktop) */}
                <div className="mt-3 mt-xl-0">
                  <LocationDropdown />
                </div>
                </div>

                {/* AUTH BUTTONS — pinned footer */}
                {!shouldHideAuthElements() && (
                  <div className="offcanvas-footer">
                  <div className="header-btn-group mt-3 mt-xl-0">
                    {userDetails?.verified ? (
                      <Link
                        to="/profile?tab=personal-details"
                        className="secondary-btn"
                        onClick={closeOffcanvasMenu}
                      >
                        {userDetails.name?.split(" ")[0] || "User"}
                      </Link>
                    ) : (
                      <>
                        <a
                          href="#"
                          className="secondary-btn"
                          onClick={handleLoginClick}
                        >
                          Register
                        </a>
                        <a
                          href="#"
                          className="secondary-btn"
                          onClick={handleLoginClick}
                        >
                          <i className="fa-solid fa-user"></i> Login
                        </a>
                      </>
                    )}
                  </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* QUICK BOOKING SECTION */}
      <div
        className="boking"
        style={{
          display: shouldHideQuickBook() ? "none" : "block",
        }}
      >
        <div className="container">
          <div className="booking-choice">
            <QuickBookNew
              closeMobileQB={() => {
                setMobileQBOpen(false);
                document.body.classList.remove("qb-open"); // 👈 ADD THIS
              }}
            />
          </div>
        </div>
      </div>

      {/* Phone Verification Popup */}
      <PhoneVerificationPopup
        isOpen={showPhonePopup}
        onClose={handleClosePhonePopup}
        onPhoneVerified={handlePhoneVerified}
        onGoogleSignIn={handleGoogleSignIn}
        onShowRegistration={handleShowRegistration}
      />

      {/* Registration Popup */}
      <RegistrationPopup
        isOpen={showRegistrationPopup}
        onClose={handleCloseRegistrationPopup}
        onRegistrationComplete={handleRegistrationComplete}
        phoneNumber={registrationPhone}
      />
      <AgeWarningModal
        isOpen={!!pendingAgeWarning}
        onClose={() => setPendingAgeWarning(null)}
        onConfirm={() => {
          navigate(`/booking/${pendingAgeWarning}`);
          setPendingAgeWarning(null);
        }}
      />

      {/* OTP Entry Popup */}
      <OTPEntryPopup
        isOpen={showOTPPopup}
        onClose={handleCloseOTPPopup}
        onOTPVerified={(user) => {
          const userWithPhone = {
            ...user,
            phone: user.phone || user.mobile || otpPhone || "",
            mobile: user.mobile || user.phone || otpPhone || "",
          };
          handleOTPVerified(userWithPhone);
        }}
        phoneNumber={otpPhone}
        onResendOTP={handleResendOTP}
      />
    </>
  );
};

export default Header;
